import React, { useState } from 'react';

export interface PermissionMatrixProps {
  permissions: string[];
  roles: { name: string; displayName: string }[];
  matrix: Record<string, string[]>;
  defaults: Record<string, string[]>;
  onChange: (matrix: Record<string, string[]>) => void;
  onSave: () => void;
  onReset: () => void;
  isSaving: boolean;
  isCustomized: boolean;
}

const PERMISSION_CATEGORIES: Record<string, string[]> = {
  Content: [
    'change_email_template',
    'change_use_case_question',
    'change_review_question',
    'change_tone_of_voice_prompt',
  ],
  Page: [
    'switch_page_draft_publish',
    'edit_public_page',
    'create_custom_mini_page',
    'absorb_external_review',
  ],
  Testimonials: ['publish_review_testimonial', 'export_review_testimonial'],
  Team: [
    'manage_team_members',
    'assign_licenses',
    'view_team_activity',
    'filter_per_end_user',
    'upload_assign_promoters_to_csm',
    'view_sales_reps_activity',
    'check_progress_1to1_call',
  ],
  Rewards: [
    'view_rewards',
    'create_modify_reward',
    'add_funds_reward',
    'request_add_funds',
    'create_customer_reward',
    'create_team_incentive',
  ],
  'CRM & Reporting': ['connect_integrate_crm', 'create_reporting_subscribe'],
  People: [
    'view_assigned_promoters',
    'view_all_promoters',
    'add_promoter',
    'add_prospect',
    'invite_promoter_prospect',
  ],
  Admin: ['add_another_admin', 'view_manage_subscription', 'edit_permission'],
};

const formatPermissionLabel = (perm: string): string =>
  perm
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  roles,
  matrix,
  defaults,
  onChange,
  onSave,
  onReset,
  isSaving,
  isCustomized,
}) => {
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const isChecked = (role: string, perm: string) =>
    matrix[role]?.includes(perm) ?? false;

  const isDifferentFromDefault = (role: string, perm: string) => {
    const currentlyChecked = isChecked(role, perm);
    const defaultChecked = defaults[role]?.includes(perm) ?? false;
    return currentlyChecked !== defaultChecked;
  };

  const handleToggle = (role: string, perm: string) => {
    const current = matrix[role] ?? [];
    const updated = current.includes(perm)
      ? current.filter((p) => p !== perm)
      : [...current, perm];
    onChange({ ...matrix, [role]: updated });
  };

  const handleResetClick = () => {
    if (isCustomized) {
      setShowResetConfirm(true);
    } else {
      onReset();
    }
  };

  const confirmReset = () => {
    setShowResetConfirm(false);
    onReset();
  };

  return (
    <div className="space-y-6">
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[200px]">
                Permission
              </th>
              {roles.map((role) => (
                <th
                  key={role.name}
                  className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[120px]"
                >
                  {role.displayName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {Object.entries(PERMISSION_CATEGORIES).map(([category, perms]) => (
              <React.Fragment key={category}>
                <tr className="bg-gray-50/70">
                  <td
                    colSpan={roles.length + 1}
                    className="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider"
                  >
                    {category}
                  </td>
                </tr>
                {perms.map((perm) => (
                  <tr key={perm} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2.5 text-sm text-gray-700">
                      {formatPermissionLabel(perm)}
                    </td>
                    {roles.map((role) => {
                      const changed = isDifferentFromDefault(role.name, perm);
                      return (
                        <td key={role.name} className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <input
                              type="checkbox"
                              checked={isChecked(role.name, perm)}
                              onChange={() => handleToggle(role.name, perm)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                            {changed && (
                              <span
                                className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"
                                title="Modified from default"
                              />
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {isCustomized && (
        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
          <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
          This vendor has custom permissions. Cells with an amber dot differ from defaults.
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={handleResetClick}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Reset to Defaults
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSaving && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reset to Defaults?</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will discard all custom permission overrides and restore the default role-permission matrix. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmReset}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
